/* eslint-disable react/prop-types */
import { BookingPricingModelType } from 'parkdepot-shared/gql/graphql'
import { useStyletron } from 'parkdepot-shared/theme'
import React, { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import FlexContainer from '../../../../components/style/FlexContainer.style'
import Icon from '../../../../components/style/Icon.style'
import { useGetUser } from '../../../../hooks/useAuth'
import { ActiveFeature, LotDurationType, Prices } from '../../../../types/lot.interface'
import { formatPriceWithCurrency } from '../../../../utils/formatPriceWithCurrency.utils'
import { getLotPricingMemoized } from '../../../../utils/getLotPricing.utils'
import { priceToString } from '../../../../utils/priceToString.utils'
import { getLotDurationType } from '../../utils/getLotDurationType.utils'
import { Container, PriceName, PriceTextContainer, PriceTextWrapper, PriceValue, Title } from './LotPrices.style'

interface Props {
    lotId?: string
    prices?: Prices[]
    enabledFeatures: ActiveFeature[]
    minBookingDurationInDays: number
    maxBookingDurationInDays: number
}

interface PriceItem {
    key: string
    value: number
}

const getPublicPricingType = (bookingType: LotDurationType | undefined) => {
    return bookingType === LotDurationType.NIGHT
        ? BookingPricingModelType.PublicNightly
        : BookingPricingModelType.Public
}

const filterPriceKeys = (priceKeys: PriceItem[]): PriceItem[] => {
    return priceKeys.filter((item, index) => {
        // Get the values of all subsequent items
        const subsequentPrices = priceKeys.slice(index + 1).map((p) => p.value)

        // If there are no subsequent items (this is the last item), keep it
        if (subsequentPrices.length === 0) return true

        // Check if the current item's value is less than the minimum of subsequent values
        const minSubsequentPrice = Math.min(...subsequentPrices)
        return item.value < minSubsequentPrice
    })
}

const getPriceItems = (
    pricing: Prices,
    bookingType: LotDurationType | undefined,
    minDuration: number,
    maxDuration: number,
    isFootballLot: boolean
    // enabledFeatures: ActiveFeature[]
): PriceItem[] => {
    const priceKeys: PriceItem[] = []

    // const isFeatureEnabled = (featureName: string) => {
    //     return enabledFeatures.some(
    //         (feature) => feature.name.toLowerCase() === featureName.toLowerCase() && feature.enabled
    //     )
    // }

    switch (bookingType) {
        case LotDurationType.SHORT:
            priceKeys.push(
                { key: 'lot.hourlyPrice', value: pricing.hour },
                { key: 'lot.dailyMaximum', value: pricing.day }
            )
            break
        case LotDurationType.LONG:
            if (minDuration < 7) priceKeys.push({ key: 'lot.dailyPrice', value: pricing.day })
            if (maxDuration > 6 && minDuration < 28) priceKeys.push({ key: 'lot.weeklyPrice', value: pricing.week })
            if (maxDuration > 27) priceKeys.push({ key: 'lot.monthlyPrice', value: pricing.month })
            break
        case LotDurationType.NIGHT:
            if (minDuration <= 1) {
                priceKeys.push({ key: isFootballLot ? 'lot.oneFootballDay' : 'lot.oneNight', value: pricing.day })
            }
            if (maxDuration > 6 && minDuration < 28) priceKeys.push({ key: 'lot.oneWeek', value: pricing.week })
            if (maxDuration > 27) priceKeys.push({ key: 'lot.oneMonth', value: pricing.month })
            break
        case LotDurationType.SUBSCRIPTION:
            priceKeys.push({ key: 'lot.monthlyPrice', value: pricing.month })
            break
        default:
            priceKeys.push(
                // ...(isFeatureEnabled(LotViewName.HOURLY) ? [{ key: 'lot.hourlyPrice', value: pricing.hour }] : []),
                ...(minDuration < 1 ? [{ key: 'lot.hourlyPrice', value: pricing.hour }] : []),
                ...(minDuration < 7 ? [{ key: 'lot.dailyPrice', value: pricing.day }] : []),
                ...(maxDuration > 6 && minDuration < 28 ? [{ key: 'lot.weeklyPrice', value: pricing.week }] : []),
                ...(maxDuration > 27 ? [{ key: 'lot.monthlyPrice', value: pricing.month }] : [])
            )
    }

    return filterPriceKeys(priceKeys)
}

// Main Component
const LotPrices: FC<Props> = ({
    lotId,
    prices = [],
    enabledFeatures,
    minBookingDurationInDays,
    maxBookingDurationInDays
}) => {
    const { t } = useTranslation()
    const [css, theme] = useStyletron()
    const { pathname } = useLocation()
    const bookingType = getLotDurationType(pathname)
    const { user } = useGetUser()

    const footballLotId = '003-405'
    const isFootballLot = lotId === footballLotId

    const privatePricingLot = useMemo(() => getLotPricingMemoized(user, lotId), [user, lotId])
    const isDiscountApplied = privatePricingLot.pricing_type !== BookingPricingModelType.Public
    const currentPricingType = isDiscountApplied ? privatePricingLot.pricing_type : getPublicPricingType(bookingType)

    const currentPricing = prices.find((p) => p.pricing_type === currentPricingType) || {
        hour: 0,
        day: 0,
        week: 0,
        month: 0,
        pricing_type: currentPricingType
    }

    const displayedPrices = useMemo(
        () =>
            getPriceItems(
                currentPricing,
                bookingType,
                minBookingDurationInDays,
                maxBookingDurationInDays,
                isFootballLot
            ),
        [currentPricing, bookingType, minBookingDurationInDays, maxBookingDurationInDays, isFootballLot]
    )

    // Extract night parking pricing if applicable
    const nightParkingEnabled = enabledFeatures.some((f) => f.name === 'night_parking' && f.enabled)
    const nightParkingPrices = nightParkingEnabled
        ? prices.find((p) => p.pricing_type === BookingPricingModelType.PublicNightly)
        : null

    const nightParkingDisplayedPrices = useMemo(
        () =>
            nightParkingPrices
                ? getPriceItems(
                      nightParkingPrices,
                      LotDurationType.NIGHT,
                      minBookingDurationInDays,
                      maxBookingDurationInDays,
                      isFootballLot
                  )
                : [],
        [nightParkingPrices, minBookingDurationInDays, maxBookingDurationInDays, isFootballLot]
    )

    const PriceText = ({ tKey, price }: { tKey: string; price: number }) => {
        if (price === 999) return null
        return (
            <FlexContainer justifycontent="space-between">
                <PriceName>{t(tKey)}</PriceName>
                <PriceValue>{formatPriceWithCurrency().format(priceToString(price))}</PriceValue>
            </FlexContainer>
        )
    }

    return (
        <Container>
            <PriceTextContainer>
                <section>
                    <FlexContainer gap="10px" alignitems="center">
                        <Icon className="wemolo-cash-2" color={theme.colors.primary} aria-hidden="true" />
                        <Title>
                            {t(
                                currentPricing.pricing_type === BookingPricingModelType.PublicNightly
                                    ? isFootballLot
                                        ? 'lot.footballDayTariff'
                                        : 'lot.nightTariffe'
                                    : 'lot.parkingTariff'
                            )}
                        </Title>
                    </FlexContainer>
                    <PriceTextWrapper>
                        {displayedPrices.map((price) => (
                            <PriceText key={price.key} tKey={price.key} price={price.value} />
                        ))}
                    </PriceTextWrapper>
                </section>

                {bookingType === undefined && nightParkingDisplayedPrices.length > 0 && (
                    <section>
                        <FlexContainer gap="10px" margintop="12px" alignitems="center">
                            <Icon className="wemolo-cash-2" color={theme.colors.primary} aria-hidden="true" />
                            <Title>{t('lot.nightTariffe')}</Title>
                        </FlexContainer>
                        <PriceTextWrapper>
                            {nightParkingDisplayedPrices.map((price) => (
                                <PriceText key={price.key} tKey={price.key} price={price.value} />
                            ))}
                        </PriceTextWrapper>
                    </section>
                )}
            </PriceTextContainer>
        </Container>
    )
}

export default LotPrices
